import { useMemo, useRef } from "react";
import { CustomModal, FormEntry } from "components";
import { ImageUploadv2 } from "components/CustomUpload/ImageUploadv2";
import { useParams } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import apiService from "services/api";
import { useNotificationContext } from "contexts";
import { Button } from "reactstrap";
import { useQueryClient } from "react-query";
import { paymentOptions } from "constants";

const initialFormState = {
	receiptNo: "",
	receipt: "",
	paymentMethod: "",
	amountPaid: "",
};

const AddPaymentModal = ({ addPaymentModalDispatch, addPaymentModalProps, hideModal }) => {
	const fileInputRef = useRef();
	const { student_id } = useParams();

	const {
		register,
		handleSubmit,
		control,
		formState: { errors, dirtyFields },
		reset,
		watch,
		setValue,
		setError,
	} = useForm({
		defaultValues: initialFormState,
	});

	const queryClient = useQueryClient();

	const { createNotification } = useNotificationContext();

	const extendedHideModal = () => {
		reset(initialFormState);
		hideModal();
	};

	const addPayment = (data) => {
		if (!data.receipt && !data.receiptNo)
			return setError("receipt", {
				message: "Either one of receipt or receipt number is required",
			});
		apiService.course.purchaseRecord.addPayment(addPaymentModalProps?.data?.id, data).then(() => {
			extendedHideModal();
			createNotification({
				type: "success",
				message: "Payment successfully added!",
			});
			queryClient.invalidateQueries("student", student_id);
		});
	};

	const remainingPrice = useMemo(() => {
		return (
			addPaymentModalProps?.data?.totalPricePaid -
			addPaymentModalProps?.data?.paymentRecords?.reduce((bufferTotal, currPaymentRecord) => bufferTotal + currPaymentRecord.amountPaid, 0)
		);
	}, [addPaymentModalProps]);

	return (
		<form onSubmit={handleSubmit(addPayment)}>
			<CustomModal
				compact
				show={addPaymentModalProps.show}
				hideModal={hideModal}
				centered
				zIndex={100}
				title="Add Payment"
				buttons={
					<Button color="success" type="submit">
						Add
					</Button>
				}
			>
				<div className="row">
					<div className="col-sm-12 mb-3">
						<small className="text-primary font-weight-bold ml-3">Remaining Amount to Pay:</small>
						<small className="text-white ml-3">HKD{remainingPrice.toLocaleString()}</small>
					</div>

					<div className="col-sm-6">
						<FormEntry
							register={register}
							name="amountPaid"
							errors={errors.amountPaid}
							formLabel="Amount Paid"
							type="number"
							placeholder="Amount Paid"
							required={true}
						/>
					</div>
					<div className="col-sm-6">
						<Controller
							rules={{
								required: {
									value: true,
									message: "Payment method is required",
								},
							}}
							control={control}
							name="paymentMethod"
							render={({ field: { ref, value, onChange } }) => (
								<Select
									inputRef={ref}
									className="react-select info"
									classNamePrefix="react-select"
									value={paymentOptions?.find((method) => value === method.value)}
									onChange={(val) => onChange(val.value)}
									options={paymentOptions || []}
									placeholder="Select payment method...."
								/>
							)}
						/>
						{errors.paymentMethod && (
							<div className="text-center">
								<small className="text-danger">{errors.paymentMethod?.message}</small>
							</div>
						)}
					</div>

					<div className="col-sm-6">
						<FormEntry
							register={register}
							name="receiptNo"
							errors={errors.receiptNo}
							formLabel="Receipt Number"
							type="text"
							placeholder="Receipt No"
						/>
					</div>
					<div className="col-sm-6">
						<FormEntry
							register={register}
							name="paymentDate"
							errors={errors.paymentDate}
							formLabel="Payment Date"
							type="date"
							placeholder="Payment Date"
						/>
					</div>
					<div className="col-sm-6">
						<FormEntry
							register={register}
							name="remarks"
							errors={errors.remarks}
							formLabel="Remarks"
							type="text"
							placeholder="Additional Remarks"
						/>
					</div>
					<div className="col-sm-6">
						<div className="d-flex justify-content-left">
							{errors.receipt && <small className="text-danger">{errors.receipt?.message}</small>}
							<ImageUploadv2
								fileInputRef={fileInputRef}
								setValue={setValue}
								required={!dirtyFields.receiptNo}
								name="receipt"
								watch={watch}
								labelText="Click here to upload Receipt"
							/>
						</div>
					</div>
				</div>
			</CustomModal>
		</form>
	);
};

export { AddPaymentModal };
